/**
 * FAZ 7 - Funnel Intelligence
 * Behavior Tracking Hook
 * 
 * İlk 30 saniye kullanıcı davranışını izler:
 * - Scroll derinliği
 * - Geçen süre
 * - Odak değişimleri
 * - Metin alanında durma
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const TRACKING_DURATION = 30000; // 30 saniye

export const useBehaviorTracking = (options = {}) => {
  const { onSegmentChange, enabled = true } = options;
  
  const [behavior, setBehavior] = useState({
    timeSpent: 0,
    scrollDepth: 0,
    maxScrollDepth: 0,
    focusLost: 0,
    textHoverTime: 0,
    isTracking: true,
    trackingComplete: false,
  });
  
  const [segment, setSegment] = useState(null); // 'escape' | 'exploring' | 'engaged'
  
  const startTimeRef = useRef(Date.now());
  const lastScrollRef = useRef(0);
  const textHoverStartRef = useRef(null);
  const trackingIntervalRef = useRef(null);
  
  // Segment hesaplama
  const calculateSegment = useCallback((timeSpent, scrollDepth) => {
    // 🟥 Kaçış Modu: <10sn, scroll <20%
    if (timeSpent < 10000 && scrollDepth < 20) {
      return 'escape';
    }
    // 🟩 İlgili / Uyanık: 30+sn, scroll 60%+
    if (timeSpent >= 30000 && scrollDepth >= 60) {
      return 'engaged';
    }
    // 🟨 Kararsız / Keşif: 10-30sn, scroll 25-60%
    if (timeSpent >= 10000 && scrollDepth >= 25) {
      return 'exploring';
    }
    // Henüz segment belirlenemedi
    return null;
  }, []);
  
  // Scroll tracking
  useEffect(() => {
    if (!enabled) return;
    
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      setBehavior(prev => ({
        ...prev,
        scrollDepth: scrollPercent,
        maxScrollDepth: Math.max(prev.maxScrollDepth, scrollPercent),
      }));
      
      lastScrollRef.current = scrollPercent;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enabled]);
  
  // Time tracking
  useEffect(() => {
    if (!enabled) return;
    
    trackingIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      
      setBehavior(prev => {
        const newBehavior = {
          ...prev,
          timeSpent: elapsed,
          isTracking: elapsed < TRACKING_DURATION,
          trackingComplete: elapsed >= TRACKING_DURATION,
        };
        return newBehavior;
      });
      
      // Segment güncelle
      const newSegment = calculateSegment(elapsed, lastScrollRef.current);
      if (newSegment && newSegment !== segment) {
        setSegment(newSegment);
        onSegmentChange?.(newSegment);
      }
      
      // 30 saniye sonra tracking durdur
      if (elapsed >= TRACKING_DURATION) {
        clearInterval(trackingIntervalRef.current);
      }
    }, 500);
    
    return () => clearInterval(trackingIntervalRef.current);
  }, [enabled, segment, calculateSegment, onSegmentChange]);
  
  // Focus tracking (tab değişimi)
  useEffect(() => {
    if (!enabled) return;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setBehavior(prev => ({
          ...prev,
          focusLost: prev.focusLost + 1,
        }));
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);
  
  // Text hover tracking
  const trackTextHover = useCallback((isHovering) => {
    if (isHovering) {
      textHoverStartRef.current = Date.now();
    } else if (textHoverStartRef.current) {
      const hoverDuration = Date.now() - textHoverStartRef.current;
      setBehavior(prev => ({
        ...prev,
        textHoverTime: prev.textHoverTime + hoverDuration,
      }));
      textHoverStartRef.current = null;
    }
  }, []);
  
  return {
    behavior,
    segment,
    trackTextHover,
    isFirstVisit: !localStorage.getItem('faz7_visited'),
    markVisited: () => localStorage.setItem('faz7_visited', Date.now().toString()),
  };
};

export default useBehaviorTracking;
